import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	Row,
	Section,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { EmptyView, SlimEmptyView } from "../../../../../Components/Empty";
import { Icon } from "../../../../../Components/Icon";
import { PaginatedGrid } from "../../../../../Components/PaginatedGrid";
import { Tile } from "../../../../../Components/Tile";
import {
	Guardian,
	GuardianDetailQuery,
	GuardianNameFragment,
	PersonFilterInput,
	useGuardianNamesAndImagesQuery,
} from "../../../../../Utilities";
import { PersonFormat } from "../../../../Format";
import { GuardianCombinedForm } from "../../../../Forms";
import { EmployeeLink, GuardianLink } from "../../../../Links";
import { GuardianUtilities } from "../../../../Utilities";
import * as AddressComponents from "../../../Address";
import * as EmailAddressComponents from "../../../EmailAddress";
import * as PhoneNumberComponents from "../../../PhoneNumber";
import * as RelationshipComponents from "../../../Relationship";
import * as StudentComponents from "../../../Student";
import { GuardianIconNameDescription } from "../../General";
import styles from "./styles.module.css";

export type BodyProps = {
	guardian: GuardianDetailQuery["guardian"];
};

export function Body({ guardian }: BodyProps) {
	return (
		<Row justify="spaced-start" align="flex-start">
			<Column justify="spaced-start" className={styles["overview-body__side-bar"]} style={{ flexShrink: 0 }}>
				<DetailsSection guardian={guardian} />
				<ContactInformation guardian={guardian} />
			</Column>

			<Column
				justify="spaced-start"
				className={styles["overview-body__main-column"]}
				style={{ flexGrow: 1, minWidth: 0 }}
			>
				<StudentsSection guardian={guardian} />
				<RelatedGuardiansSection guardian={guardian} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	guardian: GuardianDetailQuery["guardian"];
} & Partial<ClassNameProps & StyleProps>;

function DetailsSection({ guardian, className, style }: PageSectionProps) {
	return (
		<Section className={makeClassName(styles["overview-body__details"], className)} style={style}>
			<Section.Header>
				<Heading.H2 noMargin>Guardian Details</Heading.H2>

				<div className={styles["overview-body__details__edit-button__wrapper"]}>
					<DetailFormController guardian={guardian} />
				</div>
			</Section.Header>
			<Section.Body>
				<Column>
					<Row justify="space-between" className={styles["overview-body__details__detail"]}>
						<span className={styles["overview-body__details__detail__label"]}>Name</span>
						<span className={styles["overview-body__details__detail__value"]}>
							{PersonFormat.Fields.name(guardian.person.name)}
							{/* Instead of GuardianFormat.name or PersonFormat.name so that "You" is not displayed */}
						</span>
					</Row>
					{!!guardian.person.employee && (
						<Row justify="space-between" className={styles["overview-body__details__detail"]}>
							<span className={styles["overview-body__details__detail__label"]}>Employee</span>
							<span className={styles["overview-body__details__detail__value"]}>
								<EmployeeLink instance={guardian.person.employee}>View Employee Profile</EmployeeLink>
							</span>
						</Row>
					)}
				</Column>
			</Section.Body>
		</Section>
	);
}

const formStateArgs = { editMode: EditMode.WriteOnly };

type DetailFormControllerProps = {
	guardian: GuardianDetailQuery["guardian"];
};

function DetailFormController({ guardian }: DetailFormControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="link" onClick={openModal} className="button--icon">
				<Icon.Gear size={16} className="block" />
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ height: "40rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Edit {PersonFormat.Fields.name(guardian.person.name)}</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<GuardianCombinedForm.ControlledDetail
							guardian={guardian}
							guardianFormStateArgs={formStateArgs}
							guardianOnSuccess={closeModal}
							personFormStateArgs={formStateArgs}
							personOnSuccess={closeModal}
							userFormStateArgs={formStateArgs}
							userOnSuccess={closeModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

function ContactInformation({ guardian, className, style }: PageSectionProps) {
	const initialPeopleFilterForAddress = React.useMemo<PersonFilterInput>(
		() => ({
			or: [
				{
					idEQ: guardian.person.id,
				},
				{
					guardian: [GuardianUtilities.getRelatedGuardiansFilterInput(guardian.id)],
				},
			],
		}),
		[guardian.id, guardian.person.id],
	);

	return (
		<Section className={makeClassName(styles["overview-body__contact"], className)} style={style}>
			<Section.Header>
				<Heading.H2 noMargin>Contact Information</Heading.H2>
			</Section.Header>
			<Section.Body style={{ paddingLeft: 0, paddingRight: 0 }}>
				<Column justify="spaced-start">
					<EmailAddressComponents.EmailAddressPersonListSection
						person={guardian.person}
						emailAddresses={guardian.person.emailAddresses}
					/>
					<PhoneNumberComponents.PhoneNumberPersonListSection
						person={guardian.person}
						phoneNumbers={guardian.person.phoneNumbers}
					/>
					<AddressComponents.AddressListSection
						addresses={guardian.person.addresses}
						initialCreatePeopleFilter={initialPeopleFilterForAddress}
					/>
				</Column>
			</Section.Body>
		</Section>
	);
}

function StudentsSection({ guardian, className, style }: PageSectionProps) {
	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between" align="center">
					<Heading.H2 noMargin>Students</Heading.H2>

					<RelationshipComponents.RelationshipCreatePermissionRequired>
						<RelationshipComponents.RelationshipOnGuardianCreateModalController
							guardianID={guardian.id}
							variant="link"
							size="small"
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<Icon.Plus size={16} /> Add Student
							</Row>
						</RelationshipComponents.RelationshipOnGuardianCreateModalController>
					</RelationshipComponents.RelationshipCreatePermissionRequired>
				</Row>
			</Tile.Header>
			<Tile.Body style={{ display: "flex", flexDirection: "column" }}>
				<Conditional>
					<If condition={guardian.relationships.length === 0}>
						<EmptyView
							IconComponent={Icon.Student}
							primaryText="Students"
							secondaryText="No students listed"
							style={{ height: "100%" }}
						/>
					</If>
					<Else>
						<PaginatedGrid items={guardian.relationships} columns={2} rows={3} gap="1rem" style={{ flexGrow: 1 }}>
							{(relationship) => (
								<StudentRelationshipCard key={relationship.id} relationship={relationship} guardian={guardian} />
							)}
						</PaginatedGrid>
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}

type StudentRelationshipCardProps = {
	relationship: GuardianDetailQuery["guardian"]["relationships"][0];
	guardian: Pick<Guardian, "id"> & GuardianNameFragment;
};

function StudentRelationshipCard({ relationship, guardian }: StudentRelationshipCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className="button--tile" style={{ width: "100%", minWidth: "20rem" }}>
				<Row justify="space-between" align="flex-start">
					<StudentComponents.StudentIconNameDescription student={relationship.student} noLink />

					<RelationshipComponents.RelationshipPropertyIcons relationship={relationship} />
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentComponents.StudentRelationshipModal
					relationship={relationship}
					guardian={guardian}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function RelatedGuardiansSection({ guardian, className, style }: PageSectionProps) {
	const { loading, data } = useGuardianNamesAndImagesQuery({
		variables: {
			filters: {
				and: [
					{
						idNEQ: guardian.id,
					},
					GuardianUtilities.getRelatedGuardiansFilterInput(guardian.id),
				],
			},
		},
	});

	const numRelated = data?.guardianConnection.edges.length ?? 0;

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Heading.H2 noMargin>{toGrammaticalNumber("Related Guardian", numRelated, numRelated > 0)}</Heading.H2>
			</Tile.Header>
			<Tile.Body>
				<Conditional>
					<If condition={loading && !data}>Loading...</If>
					<If condition={!!data && data.guardianConnection.edges.length === 0}>
						<SlimEmptyView>No related guardians</SlimEmptyView>
					</If>
					<Else>
						<Row justify="spaced-start" horizontalSpacing="1.25rem" overflow="scroll">
							{data?.guardianConnection.edges.map(({ node }) => (
								<GuardianLink
									key={node.id}
									instance={node}
									className="link--tile link--no-text-decoration"
									style={{ minWidth: "16rem" }}
								>
									<GuardianIconNameDescription guardian={node} size="small" noLink />
								</GuardianLink>
							))}
						</Row>
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}
